import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { socialPostPath } from '../../social/socialPostPath';
import { useIsNarrowScreen } from '../work/useIsNarrowScreen';
import { usePromoteChannels } from '../hooks/usePromoteChannels';
import type { CalendarPost } from '../types';
import { dayKey, shiftAnchor, startOfDay, timeKey } from './calendarDates';
import { buildCalendarDays, EMPTY_DAY, postDayKey, postsNeedingYou, songsByUuid } from './calendarDayModel';
import { CalendarFreeSlot, freeSlotsOfDay, isUsableChannel } from './calendarFreeSlots';
import { useCalendarFilters } from './useCalendarFilters';
import { useCalendarMove } from './useCalendarMove';
import { useCalendarPostActions } from './useCalendarPostActions';
import { useCalendarRange } from './useCalendarRange';
import { useDeepLinkedPost } from './useDeepLinkedPost';

/**
 * Everything the Calendar screen holds: the address state (view, date, filters, open post), the
 * read of the range, the derived days, the open day panel and the handoffs to the Composer.
 */
export function useCalendarScreen(accountId: number) {
  const router = useSMLRouter();
  const navigate = useNavigate();
  const isNarrow = useIsNarrowScreen();
  const filters = useCalendarFilters(isNarrow);
  const range = useCalendarRange({ accountId, view: filters.view, anchor: filters.anchor });
  const { channels } = usePromoteChannels({ accountId });
  const actions = useCalendarPostActions(accountId);
  const { data } = range;

  const songs = useMemo(() => songsByUuid(data?.songs), [data?.songs]);
  const contentByDay = useMemo(() => buildCalendarDays(data, filters.filters), [data, filters.filters]);
  const needsYou = useMemo(() => postsNeedingYou(data?.posts), [data?.posts]);
  const hasChannel = useMemo(() => channels.some(isUsableChannel), [channels]);
  const move = useCalendarMove({ view: filters.view === 'week' ? 'week' : 'month', actions, songs });

  const [openDayKey, setOpenDayKey] = useState<string | null>(null);
  const selectedPost = useMemo(
    () => data?.posts.find((post) => post.uuid === filters.postUuid) ?? null,
    [data?.posts, filters.postUuid]
  );
  const panelDayKey = selectedPost === null ? openDayKey : postDayKey(selectedPost);
  const panelDay = useMemo(() => range.days.find((day) => day.key === panelDayKey) ?? null, [panelDayKey, range.days]);

  useDeepLinkedPost({ accountId, postUuid: filters.postUuid, data, setAnchor: filters.setAnchor });

  const { openPost } = filters;
  const onOpenPost = useCallback(
    (post: CalendarPost) => {
      setOpenDayKey(postDayKey(post));
      openPost(post.uuid);
    },
    [openPost]
  );
  const onOpenDay = useCallback(
    (key: string) => {
      setOpenDayKey(key);
      openPost(null);
    },
    [openPost]
  );
  const closePanel = useCallback(() => {
    setOpenDayKey(null);
    openPost(null);
  }, [openPost]);

  const slotsFor = useCallback(
    (day: Date, posts: CalendarPost[]): CalendarFreeSlot[] => freeSlotsOfDay(day, channels, posts),
    [channels]
  );
  const onAdd = useCallback(
    (day: Date, slot: CalendarFreeSlot | null) =>
      router.push(SMLRoutes.promoteComposer, {
        date: dayKey(day),
        ...(slot === null ? {} : { time: timeKey(slot.at), platform: slot.platform }),
      }),
    [router]
  );
  const newPost = useCallback(() => router.push(SMLRoutes.promoteComposer), [router]);
  const onEditInComposer = useCallback(
    (post: CalendarPost) => router.push(SMLRoutes.promoteComposer, { postUuid: post.uuid }),
    [router]
  );
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const openReleases = useCallback(() => router.push(SMLRoutes.promote), [router]);
  const openSocialPost = useCallback(
    (socialPostId: number) => {
      // eslint-disable-next-line no-void
      void navigate(socialPostPath(socialPostId));
    },
    [navigate]
  );

  const { view, anchor, setAnchor } = filters;
  const previous = useCallback(() => setAnchor(shiftAnchor(view, anchor, -1)), [anchor, setAnchor, view]);
  const next = useCallback(() => setAnchor(shiftAnchor(view, anchor, 1)), [anchor, setAnchor, view]);
  const today = useCallback(() => setAnchor(startOfDay(new Date())), [setAnchor]);
  const showNeedsYou = useCallback(() => filters.setFilter('states', ['needsYou']), [filters]);

  return {
    isNarrow,
    filters,
    range,
    channels,
    hasChannel,
    actions,
    songs,
    contentByDay,
    needsYou,
    move,
    selectedPost,
    panelDay,
    panelContent: panelDayKey === null ? EMPTY_DAY : (contentByDay.get(panelDayKey) ?? EMPTY_DAY),
    onOpenPost,
    onOpenDay,
    closePanel,
    slotsFor,
    onAdd,
    newPost,
    onEditInComposer,
    openChannels,
    openReleases,
    openSocialPost,
    previous,
    next,
    today,
    showNeedsYou,
  };
}

export type CalendarScreenState = ReturnType<typeof useCalendarScreen>;
